import { useState } from 'react';
import { X, ChevronUp, ChevronDown } from 'lucide-react';
import OwenAvatar from './OwenAvatar';
import { OWEN_CHARACTER } from '../../config/owenAI';

const OwenAssistant = ({
  message,
  tips = [],
  variant = 'inline', // 'inline', 'floating', 'fab'
  defaultExpanded = true,
  onDismiss,
  position = { bottom: 24, right: 24 },
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  // Tip item renderer
  const renderTip = (tip, idx) => (
    <div key={idx} style={{
      padding: 'var(--lk-space-3)', backgroundColor: 'var(--lk-warning-50)',
      borderRadius: 'var(--lk-radius-sm)', borderLeft: '3px solid var(--lk-warning-400)',
    }}>
      <span style={{ fontSize: 'var(--lk-text-xs)' }}>{tip}</span>
    </div>
  );

  // Floating Action Button variant - minimal presence
  if (variant === 'fab') {
    return (
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          position: 'fixed', ...position,
          width: 64, height: 64, borderRadius: '50%',
          background: 'none', border: 'none', padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: 'var(--lk-shadow-lg)', zIndex: 1000,
        }}
      >
        <div style={{ position: 'relative' }}>
          <img src="/assets/owen-avatar.png" alt="Owen" style={{ width: 64, height: 64, borderRadius: '50%', display: 'block' }} />
          {tips.length > 0 && (
            <div style={{
              position: 'absolute', top: -8, right: -8,
              width: 18, height: 18, borderRadius: '50%',
              backgroundColor: 'var(--lk-danger-500)', color: 'white',
              fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {tips.length}
            </div>
          )}
        </div>
      </button>
    );
  }

  // Floating panel variant
  if (variant === 'floating') {
    return (
      <div style={{
        position: 'fixed', ...position,
        width: 320, maxHeight: expanded ? 400 : 'auto',
        overflow: 'hidden', borderRadius: 'var(--lk-radius-xl)',
        boxShadow: 'var(--lk-shadow-xl)', zIndex: 1000,
        backgroundColor: 'var(--lk-bg-surface-1)',
      }}>
        {/* Header */}
        <div
          onClick={() => setExpanded(!expanded)}
          style={{
            padding: 'var(--lk-space-3) var(--lk-space-4)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            backgroundColor: 'var(--lk-gray-900)', color: 'white', cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
            <OwenAvatar size="small" showTooltip={false} online />
            <div>
              <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 700 }}>{OWEN_CHARACTER.name}</div>
              <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.9 }}>{OWEN_CHARACTER.title}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'white' }}>
              {expanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
            {onDismiss && (
              <button
                onClick={(e) => { e.stopPropagation(); setVisible(false); onDismiss?.(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'white' }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{
          maxHeight: expanded ? 300 : 0, overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}>
          <div style={{ padding: 'var(--lk-space-4)', maxHeight: 300, overflowY: 'auto' }}>
            {message && (
              <div style={{ fontSize: 'var(--lk-text-sm)', marginBottom: tips.length ? 'var(--lk-space-4)' : 0 }}>
                {message}
              </div>
            )}
            {tips.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
                {tips.map(renderTip)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline variant (default)
  return (
    <div style={{
      padding: 'var(--lk-space-4)', borderRadius: 'var(--lk-radius-lg)',
      border: '1px solid var(--lk-border)', backgroundColor: 'var(--lk-bg-surface-1)',
    }}>
      <div style={{ display: 'flex', gap: 'var(--lk-space-3)', alignItems: 'flex-start' }}>
        <OwenAvatar size="medium" online />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 4 }}>
            <span style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 700 }}>{OWEN_CHARACTER.name}</span>
            <span style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{OWEN_CHARACTER.title}</span>
          </div>
          {message && (
            <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)' }}>
              {message}
            </div>
          )}
          {tips.length > 0 && (
            <div style={{ marginTop: 'var(--lk-space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
              {tips.map(renderTip)}
            </div>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={16} color="var(--lk-text-tertiary)" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OwenAssistant;
